import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Sparkles, Loader2, ImageIcon, ArrowRight, Check, X, RefreshCw, Waypoints, Network, Lock, Crown } from "lucide-react";
import { toast } from "sonner";
import AminoAcidChart from "@/components/AminoAcidChart";
import NeurotransmittersChart from "@/components/NeurotransmittersChart";
import FunctionalGroupsChart from "@/components/FunctionalGroupsChart";
import EnzymeKineticsChart from "@/components/EnzymeKineticsChart";
import HormonesChart from "@/components/HormonesChart";
import MuscleTypesChart from "@/components/MuscleTypesChart";
import VitaminsChart from "@/components/VitaminsChart";

// Topic-id → reference chart component. Any topic listed here renders its
// chart above the analogy content. Kept explicit so no other topic surprises us.
const TOPIC_CHART = {
  "medpath-1a-amino-acids-peptides": AminoAcidChart,
  "medpath-5d-amino-acids-peptides-organic-chem-view": AminoAcidChart,
  "medpath-3a-nervous-system-structure-and-function": NeurotransmittersChart,
  "medpath-7a-the-nervous-system-behavioral": NeurotransmittersChart,
  "medpath-5d-aldehydes-and-ketones": FunctionalGroupsChart,
  "medpath-5d-alcohols": FunctionalGroupsChart,
  "medpath-5d-carboxylic-acids": FunctionalGroupsChart,
  "medpath-5d-acid-derivatives-anhydrides-amides-esters": FunctionalGroupsChart,
  "enzyme-kinetics": EnzymeKineticsChart,
  "medpath-1a-control-of-enzyme-kinetics": EnzymeKineticsChart,
  "medpath-5e-enzymes-kinetics-view": EnzymeKineticsChart,
  "medpath-3a-endocrine-system": HormonesChart,
  "medpath-3a-mechanisms-of-hormone-action": HormonesChart,
  "medpath-7a-the-endocrine-system-behavioral": HormonesChart,
  "medpath-1d-hormonal-regulation-integration-of-metabolism": HormonesChart,
  "medpath-3b-muscle-system-function": MuscleTypesChart,
  "medpath-3b-muscle-structure-control-of-contraction": MuscleTypesChart,
  "medpath-3b-nervous-control-of-muscle": MuscleTypesChart,
  "medpath-3b-muscle-cell-specialized": MuscleTypesChart,
  "medpath-1a-enzyme-structure-function": VitaminsChart,
  "medpath-5d-lipids-as-signaling-molecules-and-cofactors": VitaminsChart,
  "medpath-1d-metabolism-of-fatty-acids-and-proteins": VitaminsChart,
  "medpath-3b-digestive-system": VitaminsChart,
};

export default function ConceptDetail() {
  const { topicId } = useParams();
  const [topic, setTopic] = useState(null);
  const [image, setImage] = useState(null);
  const [imgLoading, setImgLoading] = useState(false);
  const [imgError, setImgError] = useState("");
  const [showStory, setShowStory] = useState(true);
  const [questions, setQuestions] = useState([]);

  const [genLoading, setGenLoading] = useState(false);

  // Subtopic analogy modal state
  const [subCache, setSubCache] = useState({});
  const [subOpen, setSubOpen] = useState(null); // index of open subtopic, or null
  const [subLoading, setSubLoading] = useState(false);
  const [subError, setSubError] = useState("");

  // Pathway diagram state (only relevant on topics flagged is_pathway_topic)
  const [pathwayLoading, setPathwayLoading] = useState(false);
  const [pathwayError, setPathwayError] = useState("");
  // Cross-discipline connections (Link It themes touching this topic)
  const [connections, setConnections] = useState([]);
  // Pathway quiz state
  const [quizOpen, setQuizOpen] = useState(false);
  const [quiz, setQuiz] = useState(null); // {label, steps: [{index, step, answered}], total, correct}
  const [quizIdx, setQuizIdx] = useState(0);
  const [quizInput, setQuizInput] = useState("");
  const [quizFeedback, setQuizFeedback] = useState(null); // {correct, correct_answer} | null
  const [quizSubmitting, setQuizSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/topics/${topicId}`).then((r) => {
      setTopic(r.data);
      if (r.data.image_url) setImage(r.data.image_url);
    }).catch(() => {});
    api.get("/questions", { params: { topic_id: topicId } })
      .then((r) => setQuestions(r.data))
      .catch(() => setQuestions([]));
    api.get(`/topics/${topicId}/subtopic-content`).then((r) => setSubCache(r.data || {})).catch(() => {});
    api.get(`/topics/${topicId}/connections`).then((r) => setConnections(r.data.themes || [])).catch(() => {});
  }, [topicId]);

  const openSubtopic = async (index, { regen = false } = {}) => {
    setSubOpen(index);
    setSubError("");
    const cached = subCache[String(index)];
    if (cached && !regen) return; // already have it
    setSubLoading(true);
    try {
      const params = regen ? { index, force: true } : { index };
      const r = await api.post(`/topics/${topicId}/subtopic-analogy`, null, { params });
      setSubCache((c) => ({ ...c, [String(index)]: r.data }));
      // Notify header credit chip that we spent 0.25.
      window.dispatchEvent(new CustomEvent("pm-billing-refresh"));
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 402) {
        const reason = typeof detail === "object" ? detail.reason : "You're out of credits. Upgrade for more.";
        toast.error(reason, { action: { label: "Upgrade", onClick: () => (window.location.href = "/app/plans") } });
        setSubError("Out of credits.");
      } else {
        setSubError(typeof detail === "string" ? detail : "Subtopic analogy failed. Try again.");
      }
    } finally {
      setSubLoading(false);
    }
  };

  const closeSubtopic = () => {
    setSubOpen(null);
    setSubError("");
  };

  const generatePathway = async ({ force = false } = {}) => {
    setPathwayLoading(true);
    setPathwayError("");
    const attempt = () => api.post(`/topics/${topicId}/pathway-diagram`, null, { params: force ? { force: true } : {} });
    try {
      let r;
      try { r = await attempt(); }
      catch (e1) { await new Promise((res) => setTimeout(res, 800)); r = await attempt(); }
      if (!r?.data?.pathway_diagram_url) {
        setPathwayError("Diagram render came back empty. Try again.");
        return;
      }
      setTopic((t) => ({ ...t, pathway_diagram_url: r.data.pathway_diagram_url }));
      window.dispatchEvent(new CustomEvent("pm-billing-refresh"));
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 402) {
        toast.error(typeof detail === "object" ? detail.reason : "Out of credits.", {
          action: { label: "Upgrade", onClick: () => (window.location.href = "/app/plans") },
        });
        setPathwayError("Out of credits.");
      } else {
        setPathwayError(typeof detail === "string" ? detail : "Pathway diagram failed after 2 tries.");
      }
    } finally { setPathwayLoading(false); }
  };

  const loadQuiz = async () => {
    try {
      const r = await api.get(`/topics/${topicId}/pathway-quiz`);
      setQuiz(r.data);
      // Jump to the first UNANSWERED step
      const firstOpen = r.data.steps.findIndex((s) => !s.answered);
      setQuizIdx(firstOpen === -1 ? 0 : firstOpen);
      setQuizInput("");
      setQuizFeedback(null);
    } catch (e) {
      toast.error("Couldn't load quiz.");
    }
  };

  const openQuiz = async () => {
    setQuizOpen(true);
    if (!quiz) await loadQuiz();
  };

  const submitGuess = async () => {
    if (!quizInput.trim() || quizSubmitting) return;
    setQuizSubmitting(true);
    try {
      const r = await api.post(`/topics/${topicId}/pathway-quiz/attempt`, {
        index: quizIdx,
        guess: quizInput.trim(),
      });
      setQuizFeedback(r.data);
      if (r.data.correct) {
        // Latch answered locally + bump correct
        setQuiz((q) => q ? {
          ...q,
          steps: q.steps.map((s, i) => i === quizIdx ? { ...s, answered: true } : s),
          correct: (q.correct || 0) + (q.steps[quizIdx].answered ? 0 : 1),
        } : q);
      }
    } catch (e) {
      toast.error("Couldn't submit guess.");
    } finally { setQuizSubmitting(false); }
  };

  const nextQuizStep = () => {
    if (!quiz) return;
    setQuizInput("");
    setQuizFeedback(null);
    // Move to next UNANSWERED step, wrap
    let next = (quizIdx + 1) % quiz.steps.length;
    for (let i = 0; i < quiz.steps.length; i++) {
      const cand = (quizIdx + 1 + i) % quiz.steps.length;
      if (!quiz.steps[cand].answered) { next = cand; break; }
    }
    setQuizIdx(next);
  };

  const [genError, setGenError] = useState("");
  const [outOfCredits, setOutOfCredits] = useState(false);

  const generateContent = async () => {
    setGenLoading(true);
    setGenError("");
    setOutOfCredits(false);
    try {
      const r = await api.post(`/topics/${topicId}/generate-content`);
      setTopic(r.data);
      window.dispatchEvent(new CustomEvent("pm-billing-refresh"));
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 402) {
        // Out-of-credits OR feature-locked — the global api interceptor
        // already surfaced the toast with the "Upgrade" CTA; surface a
        // dedicated inline paywall so the user has an obvious next step.
        setOutOfCredits(true);
        setGenError(
          typeof detail === "object" && detail?.reason
            ? detail.reason
            : "You're out of AI credits. Upgrade or grab a top-up pack to keep learning."
        );
      } else {
        setGenError(
          typeof detail === "string" ? detail :
          typeof detail === "object" && detail?.reason ? detail.reason :
          "Content generation hiccupped. Try again in a moment."
        );
      }
    } finally { setGenLoading(false); }
  };

  const generateImage = async () => {
    setImgLoading(true);
    setImgError("");
    const attempt = () => api.post(`/topics/${topicId}/generate-image`);
    try {
      let r;
      try {
        r = await attempt();
      } catch (firstErr) {
        // Auto-retry once after 800ms — most Nano Banana failures are transient.
        await new Promise((res) => setTimeout(res, 800));
        r = await attempt();
      }
      if (!r?.data?.image_url) {
        setImgError("The illustrator drew a blank. Try again?");
        return;
      }
      setImage(r.data.image_url);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setImgError(typeof d === "string" ? d : "Image gen failed after 2 tries — try again.");
    } finally { setImgLoading(false); }
  };

  if (!topic) return <div className="text-sm" style={{ color: "var(--text-muted)" }}>Loading…</div>;

  return (
    <div data-testid="concept-page">
      <Link to={`/app/subjects/${topic.subject_id}`} className="text-sm underline mb-6 inline-block" style={{ color: "var(--primary)" }} data-testid="back-to-subject">
        ← Back
      </Link>

      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: "var(--text-muted)" }}>
          {topic.category}
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-bold" data-testid="concept-name">{topic.name}</h1>
        {(() => {
          const Chart = TOPIC_CHART[topicId];
          return Chart ? <div className="mt-6"><Chart /></div> : null;
        })()}
        {topic.subtopics?.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1.5" data-testid="concept-subtopics">
            {topic.subtopics.map((sub, i) => {
              const hasCache = !!subCache[String(i)]?.analogy;
              return (
                <button
                  key={i}
                  onClick={() => openSubtopic(i)}
                  className="text-xs px-2.5 py-1 rounded-full border transition-colors hover:bg-[var(--muted)]"
                  style={{
                    borderColor: hasCache ? "var(--accent)" : "var(--border)",
                    color: hasCache ? "var(--accent)" : "var(--text-secondary)",
                    background: "var(--paper)",
                  }}
                  data-testid={`concept-subtopic-${i}`}
                  title="Tap for a mini analogy · 0.25 credit"
                >
                  {hasCache && <Sparkles size={10} className="inline mr-1" />}
                  {sub}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {!topic.analogy && (
        <div className="pm-card p-6 mb-6" data-testid="empty-topic-cta">
          {outOfCredits ? (
            <div data-testid="empty-topic-paywall">
              <div className="flex items-start gap-3 mb-4">
                <Lock size={20} style={{ color: "#c98a3b", flexShrink: 0, marginTop: 2 }} />
                <div>
                  <div className="font-display font-bold text-lg mb-1">You've used all your credits</div>
                  <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{genError}</p>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Link to="/app/plans" className="pm-btn-primary inline-flex items-center gap-2" data-testid="paywall-upgrade-btn">
                  <Crown size={14} /> Upgrade to Plus / Pro
                </Link>
                <Link to="/app/plans#credit-packs" className="pm-badge inline-flex items-center gap-2" style={{ background: "var(--muted)", padding: "8px 14px", cursor: "pointer" }} data-testid="paywall-topup-btn">
                  <Sparkles size={14} /> Buy a top-up pack
                </Link>
              </div>
            </div>
          ) : (
            <>
              <p className="text-sm mb-3" style={{ color: "var(--text-secondary)" }}>
                This topic is in the MCAT masterlist but hasn&apos;t been unlocked yet. Generate an analogy, story, and key facts using AI:
              </p>
              <button onClick={generateContent} disabled={genLoading} className="pm-btn-accent inline-flex items-center gap-2" data-testid="generate-content-btn">
                {genLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {genLoading ? "Generating…" : "Generate with AI · 0.5 credit"}
              </button>
              {genError && !outOfCredits && (
                <div className="mt-3 text-sm p-3 rounded-lg" style={{ background: "#c95e5e15", color: "#c95e5e", border: "1.5px solid #c95e5e" }} data-testid="empty-topic-error">
                  {genError}
                  <button onClick={generateContent} className="ml-3 underline font-semibold">Retry</button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Split view */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Left: analogy + image */}
        <div className="space-y-5">
          <div className="pm-card p-6" style={{ background: "var(--hl-yellow)" }} data-testid="analogy-card">
            <div className="pm-badge mb-3" style={{ background: "var(--accent)", color: "white" }}>
              <Sparkles size={12} className="mr-1.5" /> Analogy
            </div>
            <p className="font-display text-xl md:text-2xl leading-snug font-medium">{topic.analogy}</p>
          </div>

          <div className="pm-card overflow-hidden" data-testid="analogy-image-card">
            {image ? (
              <img src={image} alt={topic.name} className="w-full aspect-video object-cover" data-testid="analogy-image" />
            ) : (
              <div className="aspect-video flex flex-col items-center justify-center p-8" style={{ background: "var(--muted)" }}>
                <ImageIcon size={32} style={{ color: "var(--text-muted)" }} />
                <p className="text-sm mt-3 text-center" style={{ color: "var(--text-muted)" }}>
                  Generate a cartoon that makes this stick.
                </p>
                <button
                  onClick={generateImage}
                  disabled={imgLoading}
                  className="mt-4 pm-btn-accent flex items-center gap-2 text-sm"
                  data-testid="generate-image-btn"
                >
                  {imgLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  {imgLoading ? "Painting…" : "Generate analogy image · included with topic"}
                </button>
                {imgError && <p className="text-xs mt-2" style={{ color: "var(--weakness)" }}>{imgError}</p>}
              </div>
            )}
          </div>
        </div>

        {/* Right: story + key facts */}
        <div className="space-y-5">
          <div className="pm-card p-6" data-testid="story-card">
            <div className="flex items-center justify-between mb-3">
              <div className="pm-badge" style={{ background: "var(--hl-green)", color: "var(--primary)" }}>Story</div>
              <button
                onClick={() => setShowStory(!showStory)}
                className="text-xs underline"
                style={{ color: "var(--text-muted)" }}
                data-testid="toggle-story-btn"
              >
                {showStory ? "Hide" : "Show"}
              </button>
            </div>
            {showStory && (
              <p className="leading-relaxed text-base" style={{ color: "var(--text)" }}>{topic.story}</p>
            )}
          </div>

          <div className="pm-card p-6" data-testid="key-points-card">
            <div className="pm-badge mb-3" style={{ background: "var(--muted)", color: "var(--text)" }}>Key facts</div>
            <ul className="space-y-2">
              {topic.key_points?.map((p, i) => (
                <li key={i} className="flex gap-3 text-sm">
                  <Check size={16} className="mt-0.5 shrink-0" style={{ color: "var(--mastery)" }} />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>

          {questions.length > 0 && (
            <Link
              to={`/app/questions?topic=${topic.id}`}
              className="pm-btn-primary inline-flex items-center gap-2"
              data-testid="practice-questions-btn"
            >
              Practice {questions.length} questions <ArrowRight size={16} />
            </Link>
          )}
          <div className="flex flex-wrap gap-2 mt-3">
            <button
              onClick={async () => {
                setGenLoading(true);
                try {
                  await api.post(`/topics/${topicId}/generate-questions?variant=content`);
                  const r = await api.get("/questions", { params: { topic_id: topicId } });
                  setQuestions(r.data);
                } catch (_e) {
                  // Global api interceptor already surfaced the toast (402/5xx).
                  // Nothing else to do here — questions list unchanged.
                } finally { setGenLoading(false); }
              }}
              disabled={genLoading}
              className="pm-btn-secondary text-sm inline-flex items-center gap-2"
              data-testid="gen-content-qs-btn"
            >
              {genLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Generate 3 content questions · 0.5 credit
            </button>
            <button
              onClick={async () => {
                setGenLoading(true);
                try {
                  await api.post(`/topics/${topicId}/generate-questions?variant=application`);
                  const r = await api.get("/questions", { params: { topic_id: topicId } });
                  setQuestions(r.data);
                } catch (_e) {
                  // Global api interceptor already surfaced the toast.
                } finally { setGenLoading(false); }
              }}
              disabled={genLoading}
              className="pm-btn-secondary text-sm inline-flex items-center gap-2"
              data-testid="gen-app-qs-btn"
            >
              {genLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Generate 3 application questions · 0.5 credit
            </button>
          </div>
        </div>
      </div>

      {connections.length > 0 && (
        <div className="mt-8 pm-card p-6" data-testid="connections-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="pm-badge" style={{ background: "var(--accent)", color: "white" }}>
              <Network size={12} className="mr-1.5" /> Cross-discipline threads
            </div>
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
              This concept shows up in {connections.length} {connections.length === 1 ? "big-picture theme" : "big-picture themes"} across the MCAT.
            </p>
          </div>
          <div className="space-y-3">
            {connections.map((c) => (
              <div key={c.theme_id} className="rounded-2xl border p-4" style={{ borderColor: "var(--border)" }} data-testid={`connection-${c.theme_id}`}>
                <div className="flex items-center justify-between mb-1 gap-3 flex-wrap">
                  <Link
                    to={c.locked ? `/app/plans` : `/app/link-it`}
                    className="font-display font-bold text-sm hover:underline inline-flex items-center gap-1.5"
                    style={{ color: c.locked ? "var(--text-muted)" : "var(--primary)" }}
                    data-testid={`connection-link-${c.theme_id}`}
                  >
                    {c.locked && <Lock size={12} />} {c.theme_title}
                  </Link>
                  <span className="text-[10px] uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>
                    {c.locked ? "Plus-only" : "role in theme"}
                  </span>
                </div>
                <p className="text-xs mb-3 italic" style={{ color: "var(--text-secondary)" }}>{c.this_role}</p>
                {c.locked ? (
                  <Link
                    to="/app/plans"
                    className="pm-btn-accent inline-flex items-center gap-1.5 text-xs"
                    data-testid={`connection-unlock-${c.theme_id}`}
                  >
                    <Crown size={12} /> Unlock cross-links
                  </Link>
                ) : (
                  <div className="flex flex-wrap gap-1.5" data-testid={`connection-others-${c.theme_id}`}>
                    {c.other_topics.map((o, i) =>
                      o.topic_id ? (
                        <Link
                          key={i}
                          to={`/app/topic/${o.topic_id}`}
                          className="text-xs px-2.5 py-1 rounded-full border transition-colors hover:bg-[var(--muted)]"
                          style={{ borderColor: "var(--border)", color: "var(--text-secondary)", background: "var(--paper)" }}
                          title={o.why}
                        >
                          <span className="font-medium">{o.discipline}:</span> {o.topic_name}
                        </Link>
                      ) : (
                        <span
                          key={i}
                          className="text-xs px-2.5 py-1 rounded-full border"
                          style={{ borderColor: "var(--border)", color: "var(--text-muted)", background: "var(--paper)" }}
                        >
                          <span className="font-medium">{o.discipline}:</span> {o.topic_name}
                        </span>
                      )
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {topic.is_pathway_topic && (
        <div className="mt-8 pm-card p-6" data-testid="pathway-section">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <div className="pm-badge" style={{ background: "var(--primary)", color: "white" }}>
              <Waypoints size={12} className="mr-1.5" /> Metabolic Pathway
            </div>
            <div className="flex-1" />
            <button
              onClick={openQuiz}
              className="pm-btn-secondary text-sm inline-flex items-center gap-2"
              data-testid="pathway-quiz-btn"
              title="Quiz yourself on every enzyme in this pathway"
            >
              <Sparkles size={14} /> Quiz Mode
            </button>
            {topic.pathway_diagram_url ? (
              <button
                onClick={() => generatePathway({ force: true })}
                disabled={pathwayLoading}
                className="pm-btn-secondary text-sm inline-flex items-center gap-2"
                data-testid="regen-pathway-btn"
              >
                {pathwayLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                Regenerate (0.25 credit)
              </button>
            ) : (
              <button
                onClick={() => generatePathway()}
                disabled={pathwayLoading}
                className="pm-btn-accent text-sm inline-flex items-center gap-2"
                data-testid="gen-pathway-btn"
              >
                {pathwayLoading ? <Loader2 size={14} className="animate-spin" /> : <Waypoints size={14} />}
                {pathwayLoading ? "Rendering diagram…" : "Generate pathway diagram (0.25 credit)"}
              </button>
            )}
          </div>
          <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>
            Textbook-style diagram with every intermediate, enzyme, and cofactor labeled. Tap Quiz Mode to test yourself on each step.
          </p>
          {pathwayError && (
            <p className="text-sm mb-3" style={{ color: "var(--weakness)" }} data-testid="pathway-error">{pathwayError}</p>
          )}
          {topic.pathway_diagram_url ? (
            <div className="rounded-2xl overflow-hidden border" style={{ borderColor: "var(--border)" }}>
              <img
                src={topic.pathway_diagram_url}
                alt={`${topic.name} pathway diagram`}
                loading="lazy"
                className="w-full object-contain"
                style={{ background: "white" }}
                data-testid="pathway-image"
              />
            </div>
          ) : (
            <div className="rounded-2xl aspect-video flex flex-col items-center justify-center p-8" style={{ background: "var(--muted)" }}>
              <Waypoints size={40} style={{ color: "var(--text-muted)" }} />
              <p className="text-sm mt-3 text-center" style={{ color: "var(--text-muted)" }}>
                {pathwayLoading ? "Drawing every intermediate and enzyme…" : "No diagram yet. Tap generate to render one."}
              </p>
            </div>
          )}
        </div>
      )}

      {quizOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(10,22,40,0.72)", backdropFilter: "blur(6px)" }}
          onClick={() => setQuizOpen(false)}
          data-testid="pathway-quiz-modal"
        >
          <div
            className="pm-card max-w-xl w-full max-h-[90vh] overflow-y-auto p-6 md:p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setQuizOpen(false)}
              className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-[var(--muted)]"
              data-testid="pathway-quiz-close"
              aria-label="Close"
            >
              <X size={18} />
            </button>
            <div className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: "var(--text-muted)" }}>
              Pathway Quiz
            </div>
            <h2 className="font-display text-2xl md:text-3xl font-bold mb-1 pr-8" data-testid="pathway-quiz-title">
              {quiz?.label || topic.name}
            </h2>
            {quiz && (
              <p className="text-sm mb-5" style={{ color: "var(--text-secondary)" }}>
                {quiz.correct} of {quiz.total} answered · Step {quizIdx + 1} of {quiz.total}
              </p>
            )}
            {!quiz && (
              <div className="flex items-center gap-2 text-sm" style={{ color: "var(--text-muted)" }}>
                <Loader2 size={16} className="animate-spin" /> Loading quiz…
              </div>
            )}
            {quiz && (
              <>
                {/* Progress bar */}
                <div className="w-full h-2 rounded-full mb-6" style={{ background: "var(--muted)" }}>
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${Math.round((quiz.correct / quiz.total) * 100)}%`,
                      background: "var(--mastery)",
                    }}
                    data-testid="pathway-quiz-progress-bar"
                  />
                </div>

                <div className="pm-card p-5 mb-4" style={{ background: "var(--hl-yellow)" }} data-testid="pathway-quiz-step">
                  <div className="pm-badge mb-2" style={{ background: "var(--accent)", color: "white" }}>
                    Step {quizIdx + 1}
                    {quiz.steps[quizIdx].answered && (
                      <Check size={12} className="ml-1.5" />
                    )}
                  </div>
                  <p className="font-display text-lg leading-snug font-medium">{quiz.steps[quizIdx].step}</p>
                </div>

                <input
                  value={quizInput}
                  onChange={(e) => setQuizInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !quizFeedback && submitGuess()}
                  placeholder="Name the enzyme, channel, or structure…"
                  className="w-full px-4 py-3 rounded-xl border text-base mb-3"
                  style={{ borderColor: "var(--border)", background: "var(--paper)" }}
                  disabled={!!quizFeedback}
                  data-testid="pathway-quiz-input"
                  autoFocus
                />

                {quizFeedback && (
                  <div
                    className="pm-card p-4 mb-4"
                    style={{ background: quizFeedback.correct ? "var(--hl-green)" : "var(--hl-red, #fef2f2)" }}
                    data-testid="pathway-quiz-feedback"
                  >
                    {quizFeedback.correct ? (
                      <p className="text-sm flex items-center gap-2 font-medium" style={{ color: "var(--mastery)" }}>
                        <Check size={16} /> Correct — {quizFeedback.correct_answer}
                      </p>
                    ) : (
                      <p className="text-sm" style={{ color: "var(--weakness)" }}>
                        Not quite. The answer is <span className="font-semibold">{quizFeedback.correct_answer}</span>
                        {quizFeedback.aliases?.length > 0 && (
                          <span className="text-xs block mt-1" style={{ color: "var(--text-muted)" }}>
                            (also accepted: {quizFeedback.aliases.slice(0, 3).join(", ")})
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                )}

                <div className="flex gap-2">
                  {!quizFeedback ? (
                    <button
                      onClick={submitGuess}
                      disabled={quizSubmitting || !quizInput.trim()}
                      className="pm-btn-primary flex-1 text-sm"
                      data-testid="pathway-quiz-submit"
                    >
                      {quizSubmitting ? "Checking…" : "Submit"}
                    </button>
                  ) : (
                    <button
                      onClick={nextQuizStep}
                      className="pm-btn-primary flex-1 text-sm inline-flex items-center justify-center gap-2"
                      data-testid="pathway-quiz-next"
                    >
                      Next step <ArrowRight size={14} />
                    </button>
                  )}
                </div>

                {/* Step nav dots */}
                <div className="flex flex-wrap gap-1.5 mt-5 justify-center">
                  {quiz.steps.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => { setQuizIdx(i); setQuizInput(""); setQuizFeedback(null); }}
                      className="w-7 h-7 rounded-full text-xs font-semibold flex items-center justify-center transition-all"
                      style={{
                        background: s.answered ? "var(--mastery)" : (i === quizIdx ? "var(--accent)" : "var(--muted)"),
                        color: s.answered || i === quizIdx ? "white" : "var(--text-muted)",
                      }}
                      data-testid={`pathway-quiz-nav-${i}`}
                    >
                      {s.answered ? <Check size={12} /> : i + 1}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {subOpen !== null && (() => {
        const entry = subCache[String(subOpen)];
        const subName = topic.subtopics?.[subOpen] || "Subtopic";
        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(10,22,40,0.72)", backdropFilter: "blur(6px)" }}
            onClick={closeSubtopic}
            data-testid="subtopic-modal"
          >
            <div
              className="pm-card max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 md:p-8 relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={closeSubtopic}
                className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-[var(--muted)]"
                data-testid="subtopic-close-btn"
                aria-label="Close"
              >
                <X size={18} />
              </button>
              <div className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: "var(--text-muted)" }}>
                Subtopic mini-analogy
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-bold mb-4 pr-8" data-testid="subtopic-modal-title">
                {subName}
              </h2>
              {subLoading && (
                <div className="flex items-center gap-2 text-sm" style={{ color: "var(--text-muted)" }} data-testid="subtopic-loading">
                  <Loader2 size={16} className="animate-spin" /> Cooking up an analogy… (0.25 credit)
                </div>
              )}
              {!subLoading && subError && (
                <p className="text-sm" style={{ color: "var(--weakness)" }} data-testid="subtopic-error">{subError}</p>
              )}
              {!subLoading && entry && (
                <div className="space-y-5">
                  <div className="pm-card p-5" style={{ background: "var(--hl-yellow)" }} data-testid="subtopic-analogy-card">
                    <div className="pm-badge mb-2" style={{ background: "var(--accent)", color: "white" }}>
                      <Sparkles size={12} className="mr-1.5" /> Analogy
                    </div>
                    <p className="font-display text-lg md:text-xl leading-snug font-medium">{entry.analogy}</p>
                  </div>
                  {entry.image_url && (
                    <div className="pm-card overflow-hidden" data-testid="subtopic-image-card">
                      <img src={entry.image_url} alt={subName} loading="lazy" className="w-full aspect-video object-cover" />
                    </div>
                  )}
                  {entry.key_points?.length > 0 && (
                    <div className="pm-card p-5" data-testid="subtopic-key-points">
                      <div className="pm-badge mb-3" style={{ background: "var(--muted)", color: "var(--text)" }}>Key facts</div>
                      <ul className="space-y-2">
                        {entry.key_points.map((p, i) => (
                          <li key={i} className="flex gap-3 text-sm">
                            <Check size={16} className="mt-0.5 shrink-0" style={{ color: "var(--mastery)" }} />
                            <span>{p}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => openSubtopic(subOpen, { regen: true })}
                      className="pm-btn-secondary text-sm inline-flex items-center gap-2"
                      data-testid="subtopic-regen-btn"
                    >
                      <RefreshCw size={14} /> Regenerate (0.25 credit)
                    </button>
                    <button
                      onClick={closeSubtopic}
                      className="pm-btn-primary text-sm"
                      data-testid="subtopic-done-btn"
                    >
                      Got it
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
